import { type Component, createMemo } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { AvatarPropsType } from '~/types/Avatar'
import type { MainSizesType } from '~/types/components'

const SIZE_MAP: Record<MainSizesType, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56
}

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='

function getPixelSize(size: AvatarPropsType['size']): number {
  if (!size) return SIZE_MAP.md
  return typeof size === 'number' ? size : (SIZE_MAP[size] ?? SIZE_MAP.md)
}

function getInitial(text: string): string {
  const t = (text || '').trim()
  return t ? t[0]?.toUpperCase() : ''
}

function generateInitialDataURL(letter: string, size: number): string {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  canvas.width = size
  canvas.height = size
  if (!context) return TRANSPARENT_PIXEL

  context.fillStyle = '#6366f1'
  context.fillRect(0, 0, size, size)

  context.fillStyle = '#ffffff'
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.font = `${Math.floor(size * 0.55)}px sans-serif`
  context.fillText(letter, size / 2, size / 2)
  return canvas.toDataURL()
}

const Avatar: Component<AvatarPropsType> = props => {
  const pixelSize = getPixelSize(props.size)
  const initial = getInitial(props.alt)
  let resolvedSrc = TRANSPARENT_PIXEL
  try {
    if (props.src && String(props.src).trim() !== '') resolvedSrc = String(props.src)
    else resolvedSrc = generateInitialDataURL(initial || '', pixelSize)
  } catch {
    resolvedSrc = TRANSPARENT_PIXEL
  }
  const shapeClass = createMemo(() => (props.rounded === 'square' ? 'rounded-none' : 'rounded-full'))
  const avatarClasses = createMemo(() => twMerge('object-cover', shapeClass(), props.class))

  return (
    <img
      {...props}
      src={resolvedSrc}
      alt={props.alt}
      width={pixelSize}
      height={pixelSize}
      style={{
        width: `${pixelSize}px`,
        height: `${pixelSize}px`,
        ...(typeof props.style === 'object' && props.style !== null ? props.style : {})
      }}
      class={avatarClasses()}
      onError={e => {
        const target = e.currentTarget
        try {
          const dataUrl = generateInitialDataURL(initial || '', pixelSize)
          target.src = dataUrl
        } catch {
          target.src = TRANSPARENT_PIXEL
        }
        const h = props.onError
        if (h) {
          if (Array.isArray(h)) h.forEach(fn => typeof fn === 'function' && fn(e))
          else if (typeof h === 'function') h(e)
        }
      }}
    />
  )
}

export default Avatar
